import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { Currency, DEFAULT_LOCALE, Locale } from 'hakikat/utils';

/**
 * Service responsible for managing localization settings
 * Handles country, currency, and language preferences using cookies
 */
@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  /**
   * Inject Location for managing browser location
   */
  public readonly localization: LocalizationSettings = inject(LocalizationSettings);

}

//#region - - - - - Localization Settings - - - - -
@Injectable({
  providedIn: 'root'
})
export class LocalizationSettings {

  //#region Service Materials
  /**
   * Inject CookieService for managing browser cookies
   */
  readonly #cookieService: CookieService = inject(CookieService);

  /**
   * Inject Location for managing browser location
   */
  readonly #location: Location = inject(Location);
  //#endregion

  //#region Cookie Keys
  /**
   * Cookie key for storing country preference
   */
  static readonly COUNTRY_KEY: string = "firebase-country-override";

  /**
   * Cookie key for storing currency preference
   */
  static readonly CURRENCY_KEY: string = "firebase-currency-override";

  /**
   * Cookie key for storing language preference
   */
  static readonly LANGUAGE_KEY: string = "firebase-language-override";
  //#endregion

  //#region Settings
  /**
   * Gets the currently selected country from cookies
   * @returns The country code or undefined if not set
   */
  public get country(): string | undefined {
    return this.#cookieService.get(LocalizationSettings.COUNTRY_KEY);
  }

  /**
   * Sets the country preference in cookies
   * @param value - The country code to set
   */
  private set country(value: string | undefined) {
    if (value) this.#cookieService.set(LocalizationSettings.COUNTRY_KEY, value, { path: "/" });
    else this.#cookieService.delete(LocalizationSettings.COUNTRY_KEY, "/");
  }

  /**
   * Gets the currently selected currency from cookies
   * @returns The currency code or undefined if not set
   */
  public get currency(): string | undefined {
    return this.#cookieService.get(LocalizationSettings.CURRENCY_KEY);
  }

  /**
   * Sets the currency preference in cookies
   * @param value - The currency code to set
   */
  private set currency(value: string | undefined) {
    if (value) this.#cookieService.set(LocalizationSettings.CURRENCY_KEY, value, { path: "/" });
    else this.#cookieService.delete(LocalizationSettings.CURRENCY_KEY, "/");
  }

  /**
   * Gets the currently selected language from cookies
   * @returns The language code or undefined if not set
   */
  public get language(): string | undefined {
    return this.#cookieService.get(LocalizationSettings.LANGUAGE_KEY);
  }

  /**
   * Sets the language preference in cookies
   * @param value - The language code to set
   */
  private set language(value: string | undefined) {
    if (value) this.#cookieService.set(LocalizationSettings.LANGUAGE_KEY, value, { path: "/" });
    else this.#cookieService.delete(LocalizationSettings.LANGUAGE_KEY, "/");
  }
  //#endregion

  //#region Setters
  public setCurrencyAndReload(currency: Currency): void {
    // Set the currency code in the service.
    this.currency = currency;

    // Reload the page to apply the new currency.
    window.location.reload();
  }

  /**
   * Sets locale and redirects to the new localized URL
   * @param locale - The locale to set (e.g., 'en-US')
   */
  public setLocaleAndRedirect(locale: Locale): void {
    // Split the locale into language and country.
    [this.language, this.country] = locale.split("-");

    // Redirect to the new locale.
    window.location.href = `${locale === DEFAULT_LOCALE ? '' : '/' + locale}${this.#location.path(true) || '/'}`;
  }
  //#endregion
}

//#endregion

//#region - - - - - Theme Settings - - - - -
@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  //#region Theme Properties
  public get theme(): string {
    return this.currentTheme;
  }
  //#endregion

  //#region Theme Materials
  private currentTheme!: string;
  //#endregion

  //#region Initializers
  /**
   * Initializes theme service.
   */
  public initialize(): void {
    // Register for browser prefers-color-scheme media property changes.
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      // If stored theme is not light or dark, update application theme according to browser preference.
      const storedTheme = this.storedTheme;
      if (storedTheme !== 'dark' && storedTheme !== 'light') {
        this.setTheme(this.preferredTheme);
      }
    });

    // Set default theme to html document.
    this.setTheme(this.defaultTheme);
  }
  //#endregion

  //#region Assets Methods
  /**
   * Gets theme specific asset path.
   * 
   * @param path - The path of the asset.
   * @returns - The theme specific asset path.
   */
  public getAsset(path: string) {
    return `assets/${this.theme}/${path}`;
  }
  //#endregion

  //#region Theme Methods
  /** Returns stored user theme preference if exits, otherwise; returns system preferred theme. */
  private get defaultTheme(): string {
    return this.storedTheme ?? this.preferredTheme;
  }

  /** Returns preferred system theme from browser media api. */
  private get preferredTheme(): string {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  /** Returns user theme preference from browser local storage. */
  private get storedTheme(): string | null {
    return localStorage.getItem('theme');
  }

  /** Sets user theme preference to browser local storage. */
  private set storedTheme(theme: string) {
    localStorage.setItem('theme', theme);
  }

  /** Sets application theme. If given theme is auto, sets system preferred theme. */
  private setTheme(theme: string) {
    // Initialize new theme and check if it is different from current theme.
    const newTheme = theme === 'auto' ? this.preferredTheme : theme;
    if (newTheme === this.theme) return;

    // Set new theme to html document.
    document.documentElement.setAttribute('data-bs-theme', newTheme);

    // Update current theme property.  
    this.currentTheme = newTheme;
  }
  //#endregion
}
//#endregion